/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework from '@greenville/framework';
import env from '../../../common/env';
import utils from '../../../common/utils';
import { BERLIN_TENANT_ID_HEADER, BERLIN_TENANT_KEY_HEADER } from '../../../common/constants';
import * as constants from '../constants/ServiceConstants';
import { ENABLE_AGENT_THINKING_MSG_KEY, ENABLE_MEMORY_SAVER_KEY } from '../constants/StudioTextConstants';

export function getFeatureConfigApiBaseUrl(isStackB) {
  const v1BaseUrl = isStackB ? env.AI_ADMIN_API_BASE_URL_STACKB : env.AI_ADMIN_API_BASE_URL;
  return v1BaseUrl.replace(/\/api\/v1\/?$/i, '');
}

export async function buildFeatureConfigRequestHeaders(tenantId, tenantKey) {
  const baseHeaders = {
    [BERLIN_TENANT_ID_HEADER]: tenantId,
    [BERLIN_TENANT_KEY_HEADER]: tenantKey
  };
  return utils.resolveBerlinDirectAuthHeaders(baseHeaders);
}

export function resolveTenantKeyForGraphRequest(tenantId, tenantKeyProp = '') {
  if (tenantKeyProp) {
    return tenantKeyProp;
  }
  const tenantState = Framework.getStoreRegistry().getStore('tenantState');
  const selected = tenantState?.selectedTenant;
  if (selected?.tenantId === tenantId && selected?.tenantKey) {
    return selected.tenantKey;
  }
  return tenantState?.getTenantById?.(tenantId)?.tenantKey || '';
}

export function buildGraphRequestTenantPayload(tenantId, tenantKeyProp = '') {
  const tenantKey = resolveTenantKeyForGraphRequest(tenantId, tenantKeyProp);
  return {
    tenantId,
    ...(tenantKey ? { tenantKey } : {})
  };
}

/** Tenant whose feature config is already requested/loaded. */
let requestedFeatureConfigTenantId = null;

export function fetchFeatureConfigForTenant(tenantId, tenantKeyProp = '') {
  if (!tenantId) {
    return;
  }
  // Config is per tenant, so skip repeat requests for the same tenant unless the last one failed.
  const featureConfigModel = Framework.getStoreRegistry().getStore('featureConfigModel');
  if (requestedFeatureConfigTenantId === tenantId && !featureConfigModel?.error) {
    return;
  }
  requestedFeatureConfigTenantId = tenantId;
  const payload = buildGraphRequestTenantPayload(tenantId, tenantKeyProp);
  Framework.getEventManager().publish(constants.GET_FEATURE_CONFIG, { payload });
}

export function getTenantMemorySaverEnabled() {
  const featureConfigModel = Framework.getStoreRegistry().getStore('featureConfigModel');
  return featureConfigModel?.isTenantMemorySaverEnabled ?? false;
}

export function getTenantAgentThinkingEnabled() {
  const featureConfigModel = Framework.getStoreRegistry().getStore('featureConfigModel');
  return featureConfigModel?.isTenantAgentThinkingEnabled ?? false;
}

/** @returns {boolean|undefined} undefined when graph config has no explicit memory saver flag */
export function getGraphMemorySaverFromParams(params) {
  if (!params || typeof params !== 'object') {
    return undefined;
  }
  if (!Object.prototype.hasOwnProperty.call(params, ENABLE_MEMORY_SAVER_KEY)) {
    return undefined;
  }
  return params[ENABLE_MEMORY_SAVER_KEY] === true;
}

/** @returns {boolean|undefined} undefined when graph config has no explicit agent thinking flag */
export function getGraphAgentThinkingFromParams(params) {
  if (!params || typeof params !== 'object') {
    return undefined;
  }
  if (!Object.prototype.hasOwnProperty.call(params, ENABLE_AGENT_THINKING_MSG_KEY)) {
    return undefined;
  }
  return params[ENABLE_AGENT_THINKING_MSG_KEY] === true;
}

/**
 * Tenant feature config × graph memory saver → UI checked + control disabled.
 * @param {boolean} tenantFeatureEnabled - featureConfig.enableMemorySaver
 * @param {boolean|undefined} graphMemorySaver - graph config flag
 */
export function resolveMemorySaverUiState(tenantFeatureEnabled, graphMemorySaver) {
  if (!tenantFeatureEnabled) {
    return { checked: false, disabled: true };
  }
  if (graphMemorySaver === true) {
    return { checked: true, disabled: false };
  }
  if (graphMemorySaver === false) {
    return { checked: false, disabled: false };
  }
  return { checked: true, disabled: false };
}

export function resolveMemorySaverSaveValue(tenantFeatureEnabled, graphMemorySaver, toggleState) {
  if (!tenantFeatureEnabled) {
    return false;
  }
  if (graphMemorySaver === true || graphMemorySaver === false) {
    return graphMemorySaver;
  }
  return toggleState !== false;
}

export const resolveAgentThinkingUiState = resolveMemorySaverUiState;
export const resolveAgentThinkingSaveValue = resolveMemorySaverSaveValue;
